import { Theme, Typography } from '@mui/material';
import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import { cloudDeploymentTypeIconMap } from '../../../elements/AppFlow/utils/cloud-deployment-utils';
import DateFormat from '../../../elements/Date/DateFormat';
import ExternalLinkButton from '../../../elements/ExternalLinkButton/ExternalLinkButton';
import InfoIcon from '../../../elements/InfoIcon/InfoIcon';
import OwnerCell from '../../../elements/TableCells/OwnerCell';
import { ArtifactInfo, CloudArtifactData } from '../../types/artifacts-type';
import { getCloudSubTypeIconType } from '../../utils/artifacts-utils';
import InfoItemsWrapper from './InfoItemsWrapper';
import Section from './Section';

const ArtifactCloudData: FC<ArtifactCloudDataProps> = ({ artifact, cloud }) => {
  const { classes } = useStyles();
  const CloudTypeIcon = cloudDeploymentTypeIconMap.get(
    cloud.cloudDescription.type,
  );
  const CloudSubTypeIcon = getCloudSubTypeIconType(
    cloud.cloudDescription.subType,
  );
  const cloudType = cloud.cloudDescription?.type;
  const cloudSubType = cloud.cloudDescription?.subType;
  const account = cloud.account;
  const hash = artifact?.artifactInfo.hash || '';
  const containers = cloud.cloudDescription?.cloudEntityAttributes?.account;
  const zone = cloud.zone;
  const lastExecutionTime = new Date(cloud.lastExecutionTime);
  const lastModifiedTime = new Date(cloud.lastModifiedTime);
  const link = cloud.link;
  const cpu = cloud.cloudDescription?.cloudEntityAttributes['cpu'];
  const memory = cloud.cloudDescription?.cloudEntityAttributes['memory'];
  const os = cloud.cloudDescription?.cloudEntityAttributes['os'];
  const handler = cloud.cloudDescription?.cloudEntityAttributes['handler'];
  const runtime = cloud.cloudDescription?.cloudEntityAttributes['runtime'];
  const size = cloud.cloudDescription?.cloudEntityAttributes['size'];
  const registeredAt = new Date(
    cloud.cloudDescription?.cloudEntityAttributes?.registeredAt,
  );
  const registeredBy =
    cloud.cloudDescription?.cloudEntityAttributes?.registeredBy;

  return (
    <div className={classes.artifactRegistryInfo}>
      <InfoItemsWrapper>
        <div>
          <Typography variant='body2' color='text.secondary'>
            Cloud Type
          </Typography>
          <div className={classes.registryType}>
            {CloudTypeIcon && <CloudTypeIcon height='1rem' />}
            <Typography variant='body1' color='text.primary'>
              {cloudType}
              <ExternalLinkButton size='small' href={link} target='_blank' />
            </Typography>
          </div>
        </div>
        {cloudSubType && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Cloud Services
            </Typography>
            <div className={classes.registryType}>
              {CloudSubTypeIcon && <CloudSubTypeIcon height='1rem' />}
              <Typography variant='body1' color='text.primary'>
                {cloudSubType}
              </Typography>
            </div>
          </div>
        )}
        {zone && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Zone
              <InfoIcon
                title={
                  'The location of the artifact, aka geographic areas that cloud providers use to house its infrastructure.'
                }
                marginX={1}
              />
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {zone}
            </Typography>
          </div>
        )}
        {handler && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Handler
              <InfoIcon
                title={
                  'The name of the artifacts code entry point - this is where the artifact code starts to run'
                }
                marginX={1}
              />
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {handler}
            </Typography>
          </div>
        )}
        {runtime && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Run Time
              <InfoIcon
                title={
                  'The name of the runtime used to run the code, this is based of the code language and every language has a different type of runtime with several versions'
                }
                marginX={1}
              />
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {runtime}
            </Typography>
          </div>
        )}
        {size && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Size
              <InfoIcon
                title={'Size of the code on cloud provider'}
                marginX={1}
              />
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {size}
            </Typography>
          </div>
        )}

        {account && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Account
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {account}
            </Typography>
          </div>
        )}
        {containers && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Containers
              <InfoIcon
                title={'number of containers in the ECS task definition'}
                marginX={1}
              />
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {containers}
            </Typography>
          </div>
        )}
        {cpu && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              CPU
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {cpu}
            </Typography>
          </div>
        )}
        {memory && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Memory
              <InfoIcon
                title={'Size of memory used in the task in megabytes'}
                marginX={1}
              />
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {memory}
            </Typography>
          </div>
        )}
        {registeredAt && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Registered At{' '}
              <InfoIcon
                title={
                  'This date indicates, when this artifact was introduced to the cloud provider'
                }
                marginX={1}
              />
            </Typography>
            <DateFormat date={registeredAt} color='text.primary' bigSize />
          </div>
        )}
        {registeredBy && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Registered By
              <InfoIcon
                title={
                  'Which user uploaded the artifact to the cloud provider, the user can be a dedicated pipeline user or a regular developer'
                }
                marginX={1}
              />
            </Typography>
            <OwnerCell owners={[registeredBy]} />
          </div>
        )}
        {os && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              OS
              <InfoIcon
                title={
                  'Clouds provider operating system or runtime definition name'
                }
                marginX={1}
              />
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {os}
            </Typography>
          </div>
        )}
        {lastExecutionTime && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Last Execution Time
              <InfoIcon
                title={'when was the last time the task was executed'}
                marginX={1}
              />
            </Typography>
            <DateFormat date={lastExecutionTime} color='text.primary' bigSize />
          </div>
        )}
        {lastModifiedTime && (
          <div>
            <Typography variant='body2' color='text.secondary'>
              Last Modified Time
              <InfoIcon
                title={
                  'the task definition running the artifact latest update time (generally when we update one of the parameters, such as memory of CPU)'
                }
                marginX={1}
              />
            </Typography>
            <DateFormat date={lastModifiedTime} color='text.primary' bigSize />
          </div>
        )}
        {hash && <Section label='Hash'>{hash}</Section>}
      </InfoItemsWrapper>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  artifactRegistryInfo: {
    paddingBlock: theme.spacing(2),
  },
  registryType: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  flag: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  flagContent: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export interface ArtifactCloudDataProps {
  artifact: ArtifactInfo;
  cloud: CloudArtifactData;
}

export default ArtifactCloudData;
